import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { CompanyNotificationType, getUserCompanyNotifications } from '@/services/ws-comments/src/main/resources/rest-api';
import { countCreatives, CreativeStatus } from '@/services/ws-creatives/src/main/resources/rest-api';
import { getPersonalComments } from '@/services/ws-orders/src/main/resources/rest-api';
import {
  ScreenshotStatus as SelfBookingScreenshotStatus,
  CreativeFlag as SelfBookingCreativeFlag,
  getScreenshotCampaigns as getSelfBookingScreenshotCampaigns,
  getCreatives as getSelfBookingCreativesApprovalList,
} from '@/services/ws-selfbooking/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { usePollingInterval } from '@/js/libs/hooks/usePollingInterval';
import { useSubscribe } from '@/js/libs/observable';
import { Media } from '@/js/libs/ui';
import { useIdentityRoles } from '@/js/libs/use-auth';
import { NotActive, useMappedRequest, useRequest } from '@/js/libs/use-request';

import useLocalStorage, { StorageKey } from 'src/hooks/useLocalStorage.ts';
import useSessionStorage, { StorageKey as SessionStorageKey, StorageScope as SessionStorageScope } from 'src/hooks/useSessionStorage.ts';

export const { useNavigationContext } = createContexts({
  Navigation() {
    const { saveBoolean, loadBoolean } = useLocalStorage();
    const isMobile = useMediaQuery({ query: Media.xsMax });
    const { ADMIN, AGENCY, USER, MANAGE_DIGITAL_PRODUCTS, OPS_PUSH, YALEO_SELF_BOOKING, OPS_CHECKS, OPS_SCREENSHOTS } = useIdentityRoles();
    const [isCollapsedNav, setIsCollapsedNav] = useState(isMobile ? true : loadBoolean(StorageKey.PUZZLE_NAV_COLLAPSED));

    const unreadCommentsCountRequest = useRequest({
      apiMethod: getPersonalComments,
      params: ADMIN || AGENCY ? { showRead: false, page: 0, limit: 1 } : NotActive,
      silent: true,
    });
    const unreadCommentsCountMappedRequest = useMappedRequest(unreadCommentsCountRequest, (data) => data.total);

    const unreadSystemNotificationsCountRequest = useRequest({
      apiMethod: getUserCompanyNotifications,
      params: { showRead: false, page: 0, limit: 1 },
      silent: true,
    });
    const unreadSystemNotificationsCountMappedRequest = useMappedRequest(unreadSystemNotificationsCountRequest, (data) => data.total);

    const criticalNotificationsRequest = useRequest({
      apiMethod: getUserCompanyNotifications,
      params: { type: CompanyNotificationType.CRITICAL, showRead: false, page: 0, sort: 'startDate', order: 'asc' },
      silent: true,
    });
    const { saveString: saveDismissedNotificationId, loadString: loadDismissedNotificationId } = useSessionStorage(
      SessionStorageScope.NOTIFICATIONS_CRITICAL_BANNER,
    );

    const criticalNotificationData = useSubscribe(criticalNotificationsRequest.data$, (data) => {
      const items = data?.data.items;
      const item = items?.[items.length - 1];
      const dismissedId = loadDismissedNotificationId(SessionStorageKey.DISMISSED_NOTIFICATION_ID);

      if (!item || item.id === dismissedId) return null;

      return item;
    });

    const dismissCriticalNotification = useCallbackRef(() => {
      if (!criticalNotificationData?.id) return;
      saveDismissedNotificationId(SessionStorageKey.DISMISSED_NOTIFICATION_ID, criticalNotificationData.id);
      criticalNotificationsRequest.retry();
    });

    const countCreativesRequest = useRequest({
      apiMethod: countCreatives,
      params: !ADMIN && !(MANAGE_DIGITAL_PRODUCTS && OPS_PUSH && !USER) ? NotActive : { creativeStatus: CreativeStatus.PENDING_APPROVAL },
    });

    const articlesCount = useSubscribe(countCreativesRequest.data$, (response) => response?.data ?? 0);
    const pendingArticlesCount = useSubscribe(countCreativesRequest.pending$);

    const getSelfBookingCreativesApprovalListRequest = useRequest({
      apiMethod: getSelfBookingCreativesApprovalList,
      params: YALEO_SELF_BOOKING && (ADMIN || OPS_CHECKS) ? { flag: SelfBookingCreativeFlag.PENDING_APPROVAL } : NotActive,
      silent: true,
    });

    const getSelfBookingScreenshotCampaignsRequest = useRequest({
      apiMethod: getSelfBookingScreenshotCampaigns,
      params: YALEO_SELF_BOOKING && (ADMIN || OPS_SCREENSHOTS) ? { status: SelfBookingScreenshotStatus.NEW } : NotActive,
      silent: true,
    });

    const collapseMainNav = useCallbackRef(() => {
      saveBoolean(StorageKey.PUZZLE_NAV_COLLAPSED, !isCollapsedNav);
      setIsCollapsedNav(!isCollapsedNav);
    });

    usePollingInterval(
      () => {
        unreadSystemNotificationsCountRequest.retry();
        unreadCommentsCountRequest.retry();
        criticalNotificationsRequest.retry();
        getSelfBookingCreativesApprovalListRequest.retry();
        getSelfBookingScreenshotCampaignsRequest.retry();
      },
      { intervalMs: 60_000 },
    );

    return {
      isCollapsedNav,
      collapseMainNav,
      unreadCommentsCountRequest: unreadCommentsCountMappedRequest,
      unreadSystemNotificationsCountRequest: unreadSystemNotificationsCountMappedRequest,
      reloadUnreadSystemNotificationsCount: unreadSystemNotificationsCountRequest.retry,
      reloadUnreadCommentsCount: unreadCommentsCountRequest.retry,
      articlesCount,
      pendingArticlesCount,
      selfBookingCreativesApprovalListRequest: getSelfBookingCreativesApprovalListRequest,
      selfBookingScreenshotCampaignsRequest: getSelfBookingScreenshotCampaignsRequest,
      reloadApprovalCounter: countCreativesRequest.retry,
      criticalNotificationData,
      dismissCriticalNotification,
      reloadCriticalNotifications: criticalNotificationsRequest.retry,
    };
  },
});
